import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import type { Lang } from '@polyglot/engine';
import { LANG_ENDONYMS } from '@polyglot/engine';

/** Language label: endonym + (translated name) — never a flag. */
export function LangName({ lang, short = false }: { lang: Lang; short?: boolean }) {
  const { t } = useTranslation();
  if (short) return <span className={`lang-tag lang-${lang}`}>{lang.toUpperCase()}</span>;
  return (
    <span className="lang-name">
      <span className={`lang-dot lang-${lang}`} aria-hidden="true" />
      {LANG_ENDONYMS[lang]}
      <span className="muted"> · {t(`langNames.${lang}`)}</span>
    </span>
  );
}

export function Modal({
  title,
  children,
  onClose,
  actions,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={ref}
      >
        <h2 id="modal-title">{title}</h2>
        <div className="modal-body">{children}</div>
        <div className="modal-actions">{actions}</div>
      </div>
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="spinner-wrap" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function BackLink({ to = '/' }: { to?: string }) {
  const { t } = useTranslation();
  return (
    <Link className="back-link" href={to}>
      ← {t('nav.back')}
    </Link>
  );
}
